import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useSearchParams } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import {
	REGISTRATION_FORM,
	REGISTRATION_INPUTS,
	registrationSchema,
} from '#app/components/forms/registration-form/registration-form.tsx'
import {
	REGISTRATION_I18NEXT,
	REGISTRATION_SEARCH_PARAMS,
} from '#app/routes/($lang)._auth.registration/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseRegistrationProps = {
	app: App
}

function useRegistrationForm({ app }: UseRegistrationProps) {
	const [searchParams] = useSearchParams()
	const { t } = useTranslation(REGISTRATION_I18NEXT.routeAuthRegistration)

	const fetcherKey = REGISTRATION_FORM.fetcherKey
	const fetcher = useFetcher({ key: fetcherKey })

	const formValues = {
		[ROOT_INPUTS.subaction]: REGISTRATION_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(app.options.noJs || true),
		...(app.options.redirect && {
			[ROOT_INPUTS.redirect]: app.options.redirect,
		}),
	}
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			[REGISTRATION_INPUTS.email]:
				searchParams.get(REGISTRATION_INPUTS.email) ||
				app.options.data?.[REGISTRATION_INPUTS.email] ||
				undefined,
			...(searchParams.has(REGISTRATION_SEARCH_PARAMS.inviteId) && {
				[REGISTRATION_INPUTS.inviteId]: searchParams.get(
					REGISTRATION_SEARCH_PARAMS.inviteId,
				),
			}),
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: registrationSchema })
		},
		shouldValidate: 'onInput',
	})

	return {
		registrationFetcherKey: fetcherKey,
		registrationFetcher: fetcher,
		registrationFormValues: formValues,
		registrationForm: form,
		registrationFields: fields,
		t,
		searchParams,
	}
}

export { type UseRegistrationProps, useRegistrationForm }
